import "./styles/semantic-ui/semantic.min.css";
import "react-image-gallery/styles/css/image-gallery.css";
import "./styles/index.scss"; // must be imported last to be able to override!

import TransitionGroup from "components/TransitionGroup";
import { SmartTableProvider } from "context/SmartTableContext";
import { withLDProvider } from "launchdarkly-react-client-sdk";
import type React from "react";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { withErrorBoundary } from "react-error-boundary";
import {
  Links,
  type LinksFunction,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
} from "react-router";
import { isRouteErrorResponse, useRouteError } from "react-router";
import { Loader } from "semantic-ui-react";
import { UIProvider } from "services/UI";
import { UIMessage } from "services/UI";
import { USER_MSG } from "strings";
import { useT } from "i18n/i18n";
import { I18nHydrationScript } from "i18n/I18nHydrationScript";

export const links: LinksFunction = () => [{ rel: "icon", href: "/favicon.ico" }];
// Defaults overridden:
// - refetchOnWindowFocus: false — every mounted query refetches when the
//   browser tab regains focus by default, which makes the admin page (and
//   other paginated screens) appear to "reload" on tab switch.
// - staleTime: Infinity — cached data is never auto-considered stale, so
//   mounts and re-renders reuse the cache without issuing a network fetch.
//   To force a refresh, call `refetch()` or invalidate the query key via
//   `queryClient.invalidateQueries(...)`.
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: Number.POSITIVE_INFINITY,
    },
  },
});

export function Layout({ children }: { children: React.ReactNode }) {
  const t = useT();
  const region = import.meta.env.VITE_REGION ?? "";

  return (
    <html lang={"en-US"}>
      <head>
        <meta charSet={"UTF-8"} />
        <meta name={"viewport"} content={"width=device-width, initial-scale=1.0"} />
        <meta name={"theme-color"} content={"#000000"} />
        <meta
          name={"description"}
          content={t(
            "component.root.description",
            "Builder Management Portal for Latham Measure",
          )}
        />

        <title>
          {t("component.root.title", "Latham Measure Builder Management Portal")}
        </title>
        <Meta />
        <Links />
        <I18nHydrationScript locale={"en-US"} region={region} />
      </head>
      <body>
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export function HydrateFallback() {
  return (
    <TransitionGroup isVisible>
      <Loader />
    </TransitionGroup>
  );
}

export function ErrorBoundary(props: { error?: any }) {
  const t = useT();
  // Use either provided error prop or get from useRouteError
  const routeError = useRouteError();
  const error = props.error || routeError;
  console.log(" error -> ", error);

  if (isRouteErrorResponse(error)) {
    console.log("Handle route error responses");
    // Handle route error responses (like 404, 401, etc.)
    return (
      <UIMessage
        title={`${error.status} - ${error.statusText}`}
        msg={
          error.data?.message ||
          t(
            "component.error.somethingWentWrongWithRoute",
            "Something went wrong with this route",
          )
        }
        icon={"💥"}
      />
    );
  } else if (error instanceof Error) {
    console.log("Handle JavaScript errors");
    // Handle JavaScript errors
    return (
      <UIMessage
        title={t("component.error.error", "Error")}
        msg={
          error.message ||
          t("component.error.anUnexpectedErrorOccurred", "An unexpected error occurred")
        }
        icon={"💥"}
      />
    );
  } else {
    console.log("error -> ", error);
    console.log("Handle unknown errors");
    // Handle unknown errors
    return (
      <UIMessage
        title={t("component.error.oops", "Oops")}
        msg={
          typeof error === "object" && error !== null && "message" in error
            ? (error as { message: string }).message
            : USER_MSG.ERROR_GENERIC.msg
        }
        icon={"💥"}
      />
    );
  }
}

const withProviders = (Component: React.ComponentType) => {
  const WithErrorBoundary = withErrorBoundary(Component, {
    fallback: <UIMessage {...USER_MSG.ERROR_GENERIC} />,
  });

  return withLDProvider({
    clientSideID:
      window.VITE_LAUNCH_DARKLY_KEY ?? import.meta.env.VITE_LAUNCH_DARKLY_KEY ?? "",
  })(WithErrorBoundary);
};

export default function Root() {
  const EnhancedRoot = withProviders(() => (
    <QueryClientProvider client={queryClient}>
      <UIProvider>
        <SmartTableProvider>
          <Outlet />
        </SmartTableProvider>
      </UIProvider>
    </QueryClientProvider>
  ));

  return <EnhancedRoot />;
}
